import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { CustomTableHeadersProps, TertiaryButton, palette } from '@dev/ee-components';
import { DeleteCheckout, getCheckoutsStore } from '@/store/checkouts/actions';
import { ShowDialog, UpdateDialog } from '@/store/dialog/actions';
import { ShowDialogParams } from '@/store/dialog/types';
import { useAccessLevel } from '@/services/hooks/useAccessLevel';

import { AccessLevel } from '@dev/ee-api/ee-api-models';
import { CHECKOUT_DELETE_CONFIRMATION_TEXT } from '@/components/Checkouts/CheckoutsInterface';
import { CheckoutDeleteModalBody } from '../../components/CheckoutDeleteModalBody';
import { StatusFilters } from '../../components/StatusFilters';
import { useCheckoutParamsFromUrl } from '@/components/Checkouts/hooks/useRequestParams';

export const useTableHeaders = (): CustomTableHeadersProps[] => {
    const { t } = useTranslation();
    const checkouts = useSelector(getCheckoutsStore);
    const dispatch = useDispatch();
    const [deleteConfirmationText, setDeleteConfirmationText] = useState('');
    const { checkedCheckouts } = checkouts;
    const checkedCheckoutsLength = checkedCheckouts?.length || 0;
    const { hasAccessLevel } = useAccessLevel();
    const canModifyCheckouts = hasAccessLevel(AccessLevel.ModifyLandingPagesCheckout);
    const urlParams = useCheckoutParamsFromUrl();

    const handleCheckoutDeleteClick = () => {
        const changeDeleteConfirmationText = (text): void => {
            setDeleteConfirmationText(text); 
            dispatch(UpdateDialog({ isDisabled: text !== CHECKOUT_DELETE_CONFIRMATION_TEXT }));
        };

        const dialogParams: ShowDialogParams = {
            title: t('deletecheckout', {count: checkedCheckoutsLength}),
            destructiveLabel: t('delete'),
            onClickDestructive: (): void => {
                dispatch(DeleteCheckout(checkedCheckouts, urlParams));
                setDeleteConfirmationText('');
            },
            onClickCancel: () => setDeleteConfirmationText(''),
            cancelLabel: t('cancel'),
            body: <CheckoutDeleteModalBody
                checkedCheckouts={checkedCheckouts}
                deleteConfirmationText={deleteConfirmationText}
                setDeleteConfirmationText={setDeleteConfirmationText}
                changeDeleteConfirmationText={changeDeleteConfirmationText}
            />,
            isDisabled: deleteConfirmationText !== CHECKOUT_DELETE_CONFIRMATION_TEXT
        };
        dispatch(ShowDialog(dialogParams));
    }

    

    if (checkedCheckoutsLength) {
        const deleteButton = canModifyCheckouts ? (
            <TertiaryButton
                style={{ margin: 0, color: palette.red[6] }}
                text={t('delete')}
                onClick={handleCheckoutDeleteClick}
            />
        ) : null;
        return [
            {
                id: 'checkoutPreview',
                headerAlign: 'left',
                columnAlign: 'left',
                label: t('checkouts'),
                sortable: false
            },
            {
                id: 'checkoutName',
                headerAlign: 'left',
                columnAlign: 'left',
                label: t('name'),
            },
            {
                id: 'status',
                headerAlign: 'left',
                label: '',
                columnAlign: 'left',
            },
            {
                id: 'viewoptions',
                label: '',
                headerAlign: 'right',
                columnAlign: 'right',
                filterIcon: deleteButton,
            },
        ];
    }
    return [
        {
            id: 'checkoutPreview',
            headerAlign: 'left',
            columnAlign: 'left',
            label: '',
            sortable: false
        },
        {
            id: 'checkoutName',
            headerAlign: 'left',
            columnAlign: 'left',
            label: t('name'),
        },
        {
            id: 'status',
            headerAlign: 'left',
            label: t('status'),
            columnAlign: 'left',
            filterIcon: <StatusFilters checkouts={checkouts} />,
        },
        {
            id: 'viewoptions',
            headerAlign: 'right',
            label: '',
            columnAlign: 'right',
        },
    ];
};
